import { useQuery } from '@tanstack/react-query';

import { favoritesKeys } from '@/domain/favorites/queryKeys';
import { supabase } from '@/integrations/supabase/client';

import { useAuthUser } from './useAuthUser';

export type FavoriteId = {
  property_id: string;
  created_at: string;
};

const FAVORITE_IDS_STORAGE_KEY = 'rc:favIds';
const DEFAULT_STALE_TIME = 1000 * 60 * 5;
const DEFAULT_GC_TIME = 1000 * 60 * 30;

export function normalizeFavoriteIds(value: unknown): FavoriteId[] {
  if (!Array.isArray(value)) return [];

  return value.reduce<FavoriteId[]>((acc, item) => {
    if (!item || typeof item !== 'object') return acc;

    const row = item as Partial<FavoriteId>;
    const propertyId = typeof row.property_id === 'string' ? row.property_id.trim() : '';
    if (!propertyId) return acc;

    acc.push({
      property_id: propertyId,
      created_at:
        typeof row.created_at === 'string' && row.created_at.trim().length > 0
          ? row.created_at
          : new Date().toISOString(),
    });

    return acc;
  }, []);
}

export function readFavoriteIdsSeed(): FavoriteId[] {
  if (typeof window === 'undefined') return [];

  try {
    const raw = sessionStorage.getItem(FAVORITE_IDS_STORAGE_KEY) || localStorage.getItem(FAVORITE_IDS_STORAGE_KEY);
    if (!raw) return [];

    const parsed = JSON.parse(raw);
    return normalizeFavoriteIds(Array.isArray(parsed) ? parsed : parsed?.data);
  } catch {
    return [];
  }
}

export function persistFavoriteIdsSeed(ids: FavoriteId[]) {
  if (typeof window === 'undefined') return;

  try {
    const payload = JSON.stringify({ ts: Date.now(), data: ids });
    sessionStorage.setItem(FAVORITE_IDS_STORAGE_KEY, payload);
    localStorage.setItem(FAVORITE_IDS_STORAGE_KEY, payload);
  } catch {
    // no-op
  }
}

async function fetchFavoriteIds(): Promise<FavoriteId[]> {
  const { data, error } = await supabase.rpc('get_my_favorite_property_ids');

  if (error) {
    const message = error instanceof Error ? error.message : String(error);
    throw new Error(`Error fetching favorite ids: ${message}`);
  }

  const ids = normalizeFavoriteIds(data);
  persistFavoriteIdsSeed(ids);
  return ids;
}

export function useFavoriteIds(options: { enabled?: boolean } = {}) {
  const { enabled = true } = options;
  const userId = useAuthUser();

  return useQuery({
    queryKey: favoritesKeys.ids(userId),
    enabled: Boolean(userId) && enabled,
    staleTime: DEFAULT_STALE_TIME,
    gcTime: DEFAULT_GC_TIME,
    initialData: userId ? readFavoriteIdsSeed() : [],
    queryFn: fetchFavoriteIds,
  });
}